import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/layout/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { CheckCircle2, Receipt, Calendar, Sparkles, BookOpen, Home, User } from 'lucide-react';
import { useSubscriptionAccess } from '../../hooks/useSubscriptionAccess';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  } catch (_) {
    return iso;
  }
};

const formatAmount = (value, currency = 'INR') => {
  if (value == null) return null;
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  } catch (_) {
    return `${currency} ${value}`;
  }
};

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const { refresh: refreshSubscriptionAccess } = useSubscriptionAccess();

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!orderId) {
        setError('Missing order reference');
        setLoading(false);
        return;
      }
      try {
        const res = await axios.get(`${API}/payment/status/${orderId}`);
        if (!alive) return;
        setStatus(res.data);
        // Bust caches so newly-granted module access takes effect
        // without a hard page refresh.
        if (refreshSubscriptionAccess) refreshSubscriptionAccess();
        if (refreshUser) refreshUser();
      } catch (err) {
        if (!alive) return;
        setError(err.response?.data?.detail || 'Unable to load payment status');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <LoadingSpinner message="Confirming your payment…" />
        </div>
      </Layout>
    );
  }

  if (error || !status) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto py-12">
          <Card className="border-amber-200 bg-amber-50/40">
            <CardContent className="p-8 text-center">
              <p className="text-slate-900 font-medium mb-1">We couldn&apos;t load this payment record</p>
              <p className="text-sm text-slate-600 mb-6">{error || 'Please check your profile for the latest plan details.'}</p>
              <div className="flex justify-center gap-3">
                <Link to="/profile">
                  <Button variant="outline" data-testid="payment-success-fallback-profile">View Profile</Button>
                </Link>
                <Link to="/dashboard">
                  <Button className="bg-[#0A7EA4] hover:bg-[#08678A]" data-testid="payment-success-fallback-dashboard">Go to Dashboard</Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  // If the record is not marked paid we still land here (could be a race
  // where the verify call hasn't finalised) — show a soft state that funnels
  // to profile rather than a green checkmark we can't back up.
  const isPaid = status.status === 'paid';

  return (
    <Layout>
      <div className="max-w-2xl mx-auto py-8 space-y-6">
        {/* Hero */}
        <div className="text-center space-y-3">
          <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full ${isPaid ? 'bg-emerald-100' : 'bg-amber-100'}`}>
            {isPaid ? (
              <CheckCircle2 className="w-12 h-12 text-emerald-600" data-testid="payment-success-check-icon" />
            ) : (
              <Sparkles className="w-12 h-12 text-amber-600" />
            )}
          </div>
          <h1 className="text-3xl font-bold text-slate-900">
            {isPaid ? 'Payment Successful' : 'Payment received — finalising'}
          </h1>
          <p className="text-slate-600 max-w-md mx-auto">
            {isPaid
              ? status.is_upgrade
                ? 'Your upgrade is complete. Answers, practice sessions, and progress tracking are unlocked.'
                : 'Your plan is active. Answers, practice sessions, and progress tracking are unlocked.'
              : 'Your payment is being verified. If this state persists, please check your profile in a moment.'}
          </p>
        </div>

        {/* Receipt card */}
        <Card className="border-2 border-emerald-100">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Plan</p>
                <p className="text-lg font-semibold text-slate-900" data-testid="payment-success-plan-name">
                  {status.plan_name || '—'}
                </p>
              </div>
              {status.amount != null ? (
                <div className="text-right">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Paid</p>
                  <p className="text-lg font-semibold text-slate-900" data-testid="payment-success-amount">
                    {formatAmount(status.amount, status.currency)}
                  </p>
                </div>
              ) : null}
            </div>

            <div className="border-t pt-4 grid sm:grid-cols-2 gap-4">
              {status.expires_at ? (
                <div className="flex items-start gap-2.5">
                  <Calendar className="w-4 h-4 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-500">Valid until</p>
                    <p className="text-sm font-medium text-slate-900" data-testid="payment-success-expiry">
                      {formatDate(status.expires_at)}
                    </p>
                  </div>
                </div>
              ) : null}
              {status.payment_id ? (
                <div className="flex items-start gap-2.5">
                  <Receipt className="w-4 h-4 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-500">Payment ID</p>
                    <p className="text-sm font-mono text-slate-700 break-all" data-testid="payment-success-payment-id">
                      {status.payment_id}
                    </p>
                  </div>
                </div>
              ) : null}
            </div>

            {status.coins_redeemed > 0 || status.coupon_code ? (
              <div className="border-t pt-4 text-sm text-slate-600 space-y-1">
                {status.coupon_code ? (
                  <p>Coupon applied: <span className="font-mono text-slate-800">{status.coupon_code}</span></p>
                ) : null}
                {status.coins_redeemed > 0 ? (
                  <p>Coins redeemed: <span className="font-medium text-slate-800">{status.coins_redeemed}</span></p>
                ) : null}
              </div>
            ) : null}
          </CardContent>
        </Card>

        {/* CTAs — Dashboard (main hub), Help (how to use the platform),
            Profile (plan details + transactions). Keeps the newly-paid user
            oriented rather than shoving them straight into a feature. */}
        <div className="grid sm:grid-cols-3 gap-3">
          <Button
            variant="outline"
            className="h-auto py-4 flex-col gap-1"
            onClick={() => navigate('/dashboard')}
            data-testid="payment-success-dashboard-btn"
          >
            <Home className="w-5 h-5 text-slate-600" />
            <span className="text-sm">Dashboard</span>
          </Button>
          <Button
            variant="outline"
            className="h-auto py-4 flex-col gap-1"
            onClick={() => navigate('/help')}
            data-testid="payment-success-help-btn"
          >
            <BookOpen className="w-5 h-5 text-slate-600" />
            <span className="text-sm">Help</span>
          </Button>
          <Button
            className="h-auto py-4 flex-col gap-1 bg-[#0A7EA4] hover:bg-[#08678A]"
            onClick={() => navigate('/profile')}
            data-testid="payment-success-profile-btn"
          >
            <User className="w-5 h-5" />
            <span className="text-sm text-white">Profile</span>
          </Button>
        </div>

        <p className="text-xs text-center text-slate-500">
          A receipt has been emailed to you. You can also review all transactions under{' '}
          <Link to="/profile" className="text-[#0A7EA4] hover:underline">Profile → Transactions</Link>.
        </p>
      </div>
    </Layout>
  );
};

export default PaymentSuccessPage;
